import { useEffect, useRef, useState } from 'react';
import { useStore } from '../app/store';
import type { RouteName } from '../app/route';
import { Icon, Logo } from '../ui/icons';
import { SyncBar, SaveChip, Toast } from '../ui/components';
import { Overlays } from '../overlays/Overlays';
import { PAGES } from '../pages/registry';
import { Dashboard } from '../pages/Dashboard';

/** Screens reached from the bottom bar or the More sheet show the bottom bar; detail screens show Back instead. */
const TOP: RouteName[] = ['dashboard', 'calendar', 'customers', 'expenses', 'enquiries', 'bookings', 'reports', 'stats', 'about', 'settings', 'account'];
const TITLE: Partial<Record<RouteName, string>> = {
  dashboard: 'Today', calendar: 'Calendar', customers: 'Customers', expenses: 'Expenses', enquiries: 'Enquiries', bookings: 'Bookings',
  reports: 'Reports', stats: 'Stats & investments', about: 'About us', settings: 'Settings', account: 'Account & sync',
  booking: 'Booking', customer: 'Customer', expense: 'Expense', bill: 'Bill',
};
const PARENT: Partial<Record<RouteName, RouteName>> = { booking: 'bookings', customer: 'customers', expense: 'expenses', bill: 'bookings' };

export function MobileApp() {
  const S = useStore();
  const route = S.route.name;
  const [more, setMore] = useState(false);
  const top = TOP.includes(route);
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => { scroller.current?.scrollTo(0, 0); setMore(false); }, [route, JSON.stringify(S.route.params)]);

  let tab: string = route;
  if (['enquiries', 'bookings', 'reports', 'stats', 'about', 'settings', 'account'].includes(route)) tab = 'more';
  const Page = PAGES[route] ?? Dashboard;
  const back = () => { if (history.length > 1) history.back(); else S.nav(PARENT[route] ?? 'dashboard'); };
  const openEnq = S.state.records.filter(r => ['enquiry', 'hold'].includes(S.phase(r))).length;

  const navItem = (key: string, label: string, icon: string, go: () => void) => (
    <button key={key} className={`m-tab ${tab === key ? 'on' : ''} ${key === 'new' ? 'fab' : ''}`} onClick={go} aria-label={label}>
      <span className="m-tab-ic"><Icon name={icon} size={key === 'new' ? 24 : 21} /></span><span className="m-tab-l">{label}</span>
    </button>
  );
  const moreItems: [RouteName, string, string, number?][] = [
    ['enquiries', 'Enquiries', 'inbox', openEnq], ['bookings', 'Bookings', 'book'], ['reports', 'Reports', 'chart'], ['stats', 'Stats & investments', 'rupee'],
    ['about', 'About us', 'info'], ['settings', 'Settings', 'sliders'], ['account', 'Account & sync', 'cloud'],
  ];

  return (
    <div className={`m-shell ${top ? 'has-tabs' : 'is-sub'} m-route-${route}`}>
      <header className="m-head no-print">
        {top
          ? <button className="m-brand" onClick={() => S.nav('dashboard')} aria-label="Today"><Logo size={30} /><span>NANDANAM</span></button>
          : <button className="m-icon" onClick={back} aria-label="Back"><Icon name="left" size={20} /></button>}
        <div className="m-title">{top ? (route === 'dashboard' ? '' : TITLE[route]) : TITLE[route]}</div>
        <SaveChip />
        <button className="m-icon" onClick={() => setMore(true)} aria-label="More pages"><Icon name="grid" size={20} /></button>
      </header>
      <main className="m-scroll main" ref={scroller}>
        <SyncBar />
        <Page />
      </main>
      {top && (
        <nav className="m-tabs no-print">
          {navItem('dashboard', 'Today', 'home', () => S.nav('dashboard'))}
          {navItem('calendar', 'Calendar', 'calendar', () => S.nav('calendar'))}
          {navItem('new', 'New', 'plus', () => S.open('new', {}))}
          {navItem('customers', 'Customers', 'users', () => S.nav('customers'))}
          {navItem('expenses', 'Expense', 'receipt', () => S.nav('expenses'))}
        </nav>
      )}
      {more && (
        <div className="backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setMore(false); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="More">
            <div className="sheet-grab" />
            <div className="modal-head">
              <div style={{ flex: 1 }}><div className="kicker">Nandanam Farms</div><h2>More</h2></div>
              <button className="icon-btn" onClick={() => setMore(false)} aria-label="Close"><Icon name="x" /></button>
            </div>
            <div className="modal-body">
              {moreItems.map(([k, label, icon, n]) => (
                <button key={k} className={`m-more-row ${route === k ? 'on' : ''}`} onClick={() => { setMore(false); S.nav(k); }}>
                  <span className="tile" style={{ background: '#e1ebe9', color: 'var(--slate)', width: 42, height: 42, borderRadius: 12 }}><Icon name={icon} size={19} /></span>
                  <b>{label}</b>
                  {n ? <span className="m-more-n"><b className="num">{n}</b><span>open</span></span> : <Icon name="right" size={16} />}
                </button>
              ))}
            </div>
            <div className="modal-foot"><button className="btn primary block" onClick={() => setMore(false)}>Close</button></div>
          </div>
        </div>
      )}
      <Overlays />
      <Toast />
    </div>
  );
}
